import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { ScheduledTaskType } from '../../../../../../data/dietitian/domain/scheduled_task/scheduled_task';
import { SegmentedNavigationItemModel } from '../../../../components/segmented-navigation/components/segmented-navigation-item/segmented-navigation-item.model';
import { QuestionsViewModel } from '../../questions.facade';
import { QuestionsService, SortListType } from '../../questions.service';
import { ScheduledTaskFacade } from './scheduled-task.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-scheduled-task',
  templateUrl: './scheduled-task.component.html',
  styleUrls: ['./scheduled-task.component.scss'],
  providers: [ScheduledTaskFacade],
})
export class ScheduledTaskComponent implements OnInit, OnDestroy {
  public segmentedItems: SegmentedNavigationItemModel[] = [
    new SegmentedNavigationItemModel('encouragement', 'Encouragements'),
    new SegmentedNavigationItemModel('message', 'Messages'),
    new SegmentedNavigationItemModel('survey', 'Questionnaires'),
    new SegmentedNavigationItemModel('document', 'Documents'),
  ];
  public group: PatientGroup | undefined;
  private dietitian: Dietitian | undefined;
  private scheduledTaskStatus$: Observable<QuestionsViewModel>;
  private scheduledTaskStatusSub: Subscription;
  private selectedSortListTypeSubscription: Subscription;
  private selectedGroupSubscription: Subscription;
  private scheduledTaskEventsSubscription: Subscription;

  constructor(
    private questionsService: QuestionsService,
    private facade: ScheduledTaskFacade,
  ) {
    this.scheduledTaskStatus$ = this.facade.scheduledTaskStatus$;
    this.scheduledTaskEventsSubscription =
      this.facade.scheduledTaskEventsObserver.subscribe(() => {
        this.loadItemsCount().then();
      });
  }

  ngOnInit(): void {
    this.scheduledTaskStatusSub = this.scheduledTaskStatus$.subscribe(
      async (state) => {
        this.dietitian = state.dietitian;
        await this.loadItemsCount();
      },
    );

    this.selectedGroupSubscription =
      this.questionsService.selectedGroupEmitter.subscribe((selectedGroup) => {
        this.group = selectedGroup;
        this.loadItemsCount().then();
      });

    this.selectedSortListTypeSubscription =
      this.questionsService.selectedSortListTypeEmitter.subscribe(
        async (type) => {
          if (type == SortListType.All || type == SortListType.Archived) {
            this.group = undefined;
          }
          await this.loadItemsCount();
        },
      );
  }

  public async loadItemsCount() {
    if (!this.dietitian) {
      return;
    }

    if (
      this.questionsService.selectedSortListType == SortListType.All &&
      this.dietitian.activatedPatientCount
    ) {
      this.segmentedItems[0].itemsCount =
        await this.facade.getDietitianEncouragementCount(
          this.dietitian?.id?.toString(),
        );

      this.segmentedItems[1].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.MESSAGE,
        );

      this.segmentedItems[2].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.SURVEY,
        );

      this.segmentedItems[3].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.DOCUMENT,
        );
    } else if (
      this.questionsService.selectedSortListType == SortListType.Archived &&
      this.dietitian.archivedPatientCount
    ) {
      this.segmentedItems[0].itemsCount =
        await this.facade.getDietitianEncouragementCount(
          this.dietitian?.id?.toString(),
        );

      this.segmentedItems[1].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.MESSAGE,
        );

      this.segmentedItems[2].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.SURVEY,
        );

      this.segmentedItems[3].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByType(
          this.dietitian?.id?.toString(),
          ScheduledTaskType.DOCUMENT,
        );
    } else if (
      this.questionsService.selectedSortListType == SortListType.Group &&
      this.questionsService.selectedGroup?.activatedPatientCount
    ) {
      this.segmentedItems[0].itemsCount =
        await this.facade.getDietitianEncouragementCountByGroup(
          this.dietitian?.id?.toString(),
          this.questionsService.selectedGroup?.id.toString(),
        );

      this.segmentedItems[1].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByGroupAndType(
          this.dietitian?.id?.toString(),
          this.questionsService.selectedGroup?.id.toString(),
          ScheduledTaskType.MESSAGE,
        );

      this.segmentedItems[2].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByGroupAndType(
          this.dietitian?.id?.toString(),
          this.questionsService.selectedGroup?.id.toString(),
          ScheduledTaskType.SURVEY,
        );

      this.segmentedItems[3].itemsCount =
        await this.facade.getDietitianScheduledTaskCountByGroupAndType(
          this.dietitian?.id?.toString(),
          this.questionsService.selectedGroup?.id.toString(),
          ScheduledTaskType.DOCUMENT,
        );
    }
  }

  ngOnDestroy(): void {
    this.scheduledTaskStatusSub?.unsubscribe();
    this.selectedSortListTypeSubscription?.unsubscribe();
    this.selectedGroupSubscription?.unsubscribe();
    this.scheduledTaskEventsSubscription?.unsubscribe();
  }
}
